/** Offline-friendly export helpers — no network, no analytics. */

/** Feature flag: chart PNG export (re-enabled after branded canvas renderer). */
export const ENABLE_CHART_PNG_EXPORT = true;

/** @deprecated Prefer exportAnnualReportFromDb / section PDF exporters — print CSS is no longer the primary path. */
export function downloadViewAsPdf(documentTitle?: string) {
  const prev = document.title;
  if (documentTitle) document.title = documentTitle;
  requestAnimationFrame(() => {
    window.print();
    if (documentTitle) {
      setTimeout(() => {
        document.title = prev;
      }, 500);
    }
  });
}

/**
 * Canvas fillStyle/strokeStyle reject modern space-separated HSL (`hsl(32 70% 52%)`).
 * Convert to comma form (and leave hex untouched) so PDF/PNG charts keep brand colors.
 */
export function toCanvasColor(c: string): string {
  const trimmed = c.trim();
  const spaceHsl = trimmed.match(
    /^hsla?\(\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*(?:\/\s*([\d.]+%?)\s*)?\)$/i
  );
  if (spaceHsl) {
    const [, h, s, l, a] = spaceHsl;
    if (a !== undefined) {
      const alpha = a.endsWith("%") ? String(parseFloat(a) / 100) : a;
      return `hsla(${h}, ${s}%, ${l}%, ${alpha})`;
    }
    return `hsl(${h}, ${s}%, ${l}%)`;
  }
  // CSS variable components already expanded as "H S% L%"
  const components = trimmed.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (components) {
    return `hsl(${components[1]}, ${components[2]}%, ${components[3]}%)`;
  }
  return trimmed;
}

function withAlpha(color: string, alpha: number): string {
  const safe = toCanvasColor(color);
  const hex = safe.match(/^#([0-9a-f]{6})$/i);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const hsl = safe.match(/^hsl\(([^)]+)\)$/i);
  if (hsl) return `hsla(${hsl[1]}, ${alpha})`;
  return safe;
}

/** In-app chart coral (Monthly Cash Flow expense bars/lines). */
export const CHART_EXPENSE_CORAL = "hsl(10, 65%, 58%)";
/** In-app Net Worth assets / debts strokes. */
export const CHART_ASSETS_GREEN = "#22c55e";
export const CHART_DEBTS_RED = "#ef4444";
/**
 * Net Worth trend line — fixed slate/navy, never inherits theme accent.
 * Stays distinct from Assets (green) when the user picks a mint/green accent.
 */
export const CHART_NET_WORTH = "#1e293b";
export const CHART_NET_WORTH_DASH: number[] = [6, 4];

export type ThemeChartColors = {
  income: string;
  expense: string;
  primary: string;
  muted: string;
  border: string;
  foreground: string;
  background: string;
  destructive: string;
  assets: string;
  /** Fixed theme-independent Net Worth series color */
  netWorth: string;
};

/**
 * Build chart palette from CSS variable values (testable without DOM).
 * Income / Net Worth / primary → `--primary` (active accent).
 * Expense → same coral as MonthlyCashFlowChart.
 * Assets / Debts → same hex as NetWorthPage.
 */
export function buildThemeChartColors(
  getVar: (name: string) => string
): ThemeChartColors {
  const fromVar = (name: string, fallback: string) => {
    const raw = getVar(name).trim();
    if (!raw) return toCanvasColor(fallback);
    return toCanvasColor(raw.includes("hsl") || raw.startsWith("#") ? raw : `hsl(${raw})`);
  };
  const primary = fromVar("--primary", "hsl(176, 45%, 34%)");
  return {
    primary,
    income: primary,
    expense: toCanvasColor(CHART_EXPENSE_CORAL),
    // Match NetWorthPage stroke="#ef4444" (not a generic chart default)
    destructive: toCanvasColor(CHART_DEBTS_RED),
    muted: fromVar("--muted-foreground", "hsl(215, 16%, 47%)"),
    border: fromVar("--border", "hsl(214, 32%, 91%)"),
    foreground: fromVar("--foreground", "hsl(222, 47%, 11%)"),
    background: "#ffffff",
    assets: CHART_ASSETS_GREEN,
    netWorth: CHART_NET_WORTH,
  };
}

/** Resolve live theme colors used by in-app charts + PDF embeds (never Chart.js defaults). */
export function resolveThemeChartColors(): ThemeChartColors {
  if (typeof document === "undefined") {
    return buildThemeChartColors(() => "");
  }
  const root = getComputedStyle(document.documentElement);
  return buildThemeChartColors((name) => root.getPropertyValue(name));
}

export interface ChartSeries {
  label: string;
  color: string;
  values: number[];
  /** Optional dashed stroke (Net Worth trend). */
  dashed?: boolean;
}

export interface CanvasChartExportOptions {
  filename: string;
  /** X labels already cropped to the populated range */
  labels: string[];
  series: ChartSeries[];
  /** e.g. "BudgetOS · Jan–Aug 2026" */
  watermark: string;
  title?: string;
  width?: number;
  height?: number;
  kind?: "bars" | "lines" | "area";
  formatTick?: (n: number) => string;
}

function drawLegend(
  ctx: CanvasRenderingContext2D,
  series: ChartSeries[],
  x: number,
  y: number
): number {
  ctx.font = "600 12px system-ui, sans-serif";
  let cx = x;
  for (const s of series) {
    ctx.fillStyle = toCanvasColor(s.color);
    ctx.beginPath();
    ctx.arc(cx + 5, y, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#374151";
    ctx.fillText(s.label, cx + 14, y + 4);
    cx += 14 + ctx.measureText(s.label).width + 18;
  }
  return y + 18;
}

/**
 * Brand-faithful canvas chart — returns a PNG data URL (for PDF embed or download).
 */
export function renderCanvasChartToDataUrl(opts: CanvasChartExportOptions): string | null {
  // Default 16:9 — PDF embed caps to the same aspect so bars/lines aren’t stretched flat.
  const width = opts.width ?? 960;
  const height = opts.height ?? 540;
  const kind = opts.kind ?? "bars";
  const pad = { top: 56, right: 28, bottom: 64, left: 64 };
  const colors = resolveThemeChartColors();
  const series = opts.series.map((s) => ({ ...s, color: toCanvasColor(s.color) }));

  const canvas = document.createElement("canvas");
  const scale = 2;
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.scale(scale, scale);

  ctx.fillStyle = colors.background;
  ctx.fillRect(0, 0, width, height);

  if (opts.title) {
    ctx.fillStyle = colors.foreground;
    ctx.font = "600 16px system-ui, sans-serif";
    ctx.fillText(opts.title, pad.left, 28);
  }

  drawLegend(ctx, series, pad.left, opts.title ? 44 : 28);

  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const n = Math.max(1, opts.labels.length);
  const allVals = series.flatMap((s) => s.values);
  const maxV = Math.max(...allVals, 1) * 1.12;
  const minV = Math.min(0, ...allVals);
  const span = maxV - minV || 1;

  const yAt = (v: number) => pad.top + plotH - ((v - minV) / span) * plotH;
  const formatTick =
    opts.formatTick ??
    ((v: number) =>
      new Intl.NumberFormat(undefined, {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(v));

  const ticks = 5;
  ctx.strokeStyle = colors.border;
  ctx.lineWidth = 1;
  ctx.font = "11px system-ui, sans-serif";
  ctx.fillStyle = colors.muted;
  for (let i = 0; i <= ticks; i++) {
    const v = minV + (span * i) / ticks;
    const y = yAt(v);
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + plotW, y);
    ctx.stroke();
    ctx.fillText(formatTick(v), 10, y + 4);
  }

  const gap = plotW / n;
  opts.labels.forEach((lab, i) => {
    const x = pad.left + gap * i + gap / 2;
    ctx.fillStyle = colors.muted;
    ctx.font = "11px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(lab, x, height - pad.bottom + 22);
    ctx.textAlign = "left";
  });

  if (kind === "bars") {
    const seriesCount = series.length;
    const cluster = gap * 0.62;
    const barW = cluster / seriesCount - 2;
    series.forEach((s, si) => {
      s.values.forEach((v, i) => {
        const x0 = pad.left + gap * i + (gap - cluster) / 2 + si * (barW + 2);
        const y0 = yAt(v);
        const yBase = yAt(0);
        const top = Math.min(y0, yBase);
        const h = Math.abs(yBase - y0);
        ctx.fillStyle = s.color;
        ctx.fillRect(x0, top, Math.max(2, barW), Math.max(1, h));
      });
    });
  } else {
    series.forEach((s) => {
      ctx.beginPath();
      s.values.forEach((v, i) => {
        const x = pad.left + gap * i + gap / 2;
        const y = yAt(v);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      if (kind === "area") {
        const lastX = pad.left + gap * (s.values.length - 1) + gap / 2;
        const firstX = pad.left + gap / 2;
        ctx.lineTo(lastX, yAt(0));
        ctx.lineTo(firstX, yAt(0));
        ctx.closePath();
        ctx.fillStyle = withAlpha(s.color, 0.2);
        ctx.fill();
        ctx.beginPath();
        s.values.forEach((v, i) => {
          const x = pad.left + gap * i + gap / 2;
          const y = yAt(v);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
      }
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 2.5;
      ctx.setLineDash(s.dashed ? CHART_NET_WORTH_DASH : []);
      ctx.stroke();
      ctx.setLineDash([]);
    });
  }

  ctx.fillStyle = colors.muted;
  ctx.font = "500 11px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(opts.watermark, pad.left, height - 14);
  ctx.textAlign = "right";
  ctx.fillText("Offline · no tracking", width - pad.right, height - 14);

  return canvas.toDataURL("image/png");
}

export function exportCanvasChartPng(opts: CanvasChartExportOptions): void {
  const dataUrl = renderCanvasChartToDataUrl(opts);
  if (!dataUrl) return;
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = opts.filename.endsWith(".png") ? opts.filename : `${opts.filename}.png`;
  a.click();
}

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

export function renderDonutChartToDataUrl(opts: {
  filename?: string;
  title: string;
  slices: DonutSlice[];
  watermark: string;
  emptyNote?: string;
  emptyHint?: string;
}): string | null {
  const width = 720;
  const height = 480;
  const scale = 2;
  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.scale(scale, scale);
  const colors = resolveThemeChartColors();

  ctx.fillStyle = colors.background;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = colors.foreground;
  ctx.font = "600 16px system-ui, sans-serif";
  ctx.fillText(opts.title, 32, 32);

  const slices = opts.slices.filter((s) => s.value > 0);
  const cx = 220;
  const cy = 250;
  const rOut = 110;
  const rIn = 62;

  if (slices.length === 0) {
    ctx.beginPath();
    ctx.arc(cx, cy, rOut, 0, Math.PI * 2);
    ctx.fillStyle = colors.border;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, rIn, 0, Math.PI * 2);
    ctx.fillStyle = colors.background;
    ctx.fill();
    ctx.fillStyle = colors.muted;
    ctx.font = "500 14px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(opts.emptyNote ?? "No spending in this period", cx, cy + 5);
    ctx.textAlign = "left";
    ctx.font = "13px system-ui, sans-serif";
    ctx.fillText(
      opts.emptyHint ?? "Needs / Wants / Savings will appear when this month has expenses.",
      320,
      200
    );
  } else {
    const total = slices.reduce((s, x) => s + x.value, 0) || 1;
    let angle = -Math.PI / 2;
    slices.forEach((slice) => {
      const sweep = (slice.value / total) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, rOut, angle, angle + sweep);
      ctx.closePath();
      ctx.fillStyle = toCanvasColor(slice.color);
      ctx.fill();
      angle += sweep;
    });
    ctx.beginPath();
    ctx.arc(cx, cy, rIn, 0, Math.PI * 2);
    ctx.fillStyle = colors.background;
    ctx.fill();

    let ly = 120;
    slices.forEach((slice) => {
      ctx.fillStyle = toCanvasColor(slice.color);
      ctx.beginPath();
      ctx.arc(420, ly, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = colors.foreground;
      ctx.font = "13px system-ui, sans-serif";
      const pct = ((slice.value / total) * 100).toFixed(0);
      ctx.fillText(`${slice.label}  ·  ${pct}%`, 434, ly + 4);
      ly += 28;
    });
  }

  ctx.fillStyle = colors.muted;
  ctx.font = "500 11px system-ui, sans-serif";
  ctx.fillText(opts.watermark, 32, height - 16);

  return canvas.toDataURL("image/png");
}

export function exportDonutChartPng(opts: {
  filename: string;
  title: string;
  slices: DonutSlice[];
  watermark: string;
}): void {
  const dataUrl = renderDonutChartToDataUrl(opts);
  if (!dataUrl) return;
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = opts.filename.endsWith(".png") ? opts.filename : `${opts.filename}.png`;
  a.click();
}

/** Crop month rows to first..last populated index (inclusive). Keeps at least one row. */
export function cropToPopulatedRange<T extends { income?: number; expense?: number; value?: number; Assets?: number; Debts?: number; NetWorth?: number; balance?: number }>(
  rows: T[],
  isPopulated: (row: T) => boolean
): T[] {
  let first = -1;
  let last = -1;
  rows.forEach((r, i) => {
    if (isPopulated(r)) {
      if (first < 0) first = i;
      last = i;
    }
  });
  if (first < 0) return rows.slice(0, 1);
  return rows.slice(first, last + 1);
}

export function periodLabelFromMonths(labels: string[], year: number): string {
  if (!labels.length) return `BudgetOS · ${year}`;
  if (labels.length === 1) return `BudgetOS · ${labels[0]} ${year}`;
  return `BudgetOS · ${labels[0]}–${labels[labels.length - 1]} ${year}`;
}

/** Build a CSV string from computed dashboard numbers (for export parity tests). */
export function dashboardCsvPayload(values: {
  income: number;
  expense: number;
  leftover: number;
  savingsRate: number;
  actualSaved: number;
}): string {
  return [
    "Metric,Value",
    `Money In,${values.income}`,
    `Money Out,${values.expense}`,
    `Leftover,${values.leftover}`,
    `Savings Rate %,${values.savingsRate}`,
    `Total Saved,${values.actualSaved}`,
  ].join("\n");
}
